import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { parseThemeApiResponse, siteThemeAdminUrl } from "../ThemeManagement/themeApiUtils";

const STEPS = [
  { id: 1, title: "Install theme", key: "install" },
  { id: 2, title: "Website domain", key: "website" },
  { id: 3, title: "DNS setup", key: "dns" },
  { id: 4, title: "Verify domain", key: "verify" },
  { id: 5, title: "Provision site", key: "provision" },
  { id: 6, title: "Website ready", key: "done" },
];

const stripProtocol = (value) =>
  String(value || "")
    .trim()
    .replace(/^https?:\/\//i, "")
    .replace(/\/+$/, "");

const ThemeInstallWizard = ({ token, theme, installation, onClose, onComplete }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(installation ? 2 : 1);
  const [websiteUrl, setWebsiteUrl] = useState(stripProtocol(installation?.website_url || ""));
  const [status, setStatus] = useState(null);
  const [installRow, setInstallRow] = useState(installation || null);
  const [loading, setLoading] = useState(false);
  const [provisioning, setProvisioning] = useState(null);

  const loadStatus = async () => {
    try {
      const response = await fetch(siteThemeAdminUrl("/onboarding/status"), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const res = await parseThemeApiResponse(response);
      if (response.ok && !res.isError) {
        setStatus(res.data);
        setWebsiteUrl(stripProtocol(res.data.website_url || ""));
        setInstallRow(res.data.installation);
        if (res.data.provisioning) setProvisioning(res.data.provisioning);

        const live =
          res.data.provisioning?.is_live ||
          (res.data.onboarding_completed && res.data.is_active);
        const provisioningActive =
          res.data.provisioning &&
          !res.data.provisioning.is_live &&
          !["FAILED", "ACTIVE"].includes(res.data.provisioning.provision_status);

        if (live) setStep(6);
        else if (provisioningActive) setStep(5);
        else if (res.data.domain_verified || res.data.installation?.domain_verified) setStep(4);
        else if (res.data.website_url) setStep(3);
        else if (res.data.installation) setStep(2);

        return res.data;
      }
    } catch (error) {
      toast.error(error.message);
    }
    return null;
  };

  useEffect(() => {
    loadStatus();
  }, []);

  useEffect(() => {
    if (step !== 5) return undefined;
    const timer = setInterval(async () => {
      const data = await loadStatus();
      if (data?.provisioning?.is_live) {
        toast.success("Website is live!");
        onComplete?.();
      }
      if (data?.provisioning?.provision_status === "FAILED") {
        toast.error(data.provisioning.last_error || "Provisioning failed");
      }
    }, 4000);
    return () => clearInterval(timer);
  }, [step]);

  const handleInstall = async () => {
    setLoading(true);
    try {
      const response = await fetch(siteThemeAdminUrl("/install"), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ theme_id: theme.id, activate: false }),
      });
      const res = await parseThemeApiResponse(response);
      if (response.ok && !res.isError) {
        toast.success("Theme installed");
        setInstallRow(res.data);
        setStep(2);
        loadStatus();
      } else {
        toast.error(res.message);
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveWebsite = async () => {
    setLoading(true);
    try {
      const response = await fetch(siteThemeAdminUrl("/onboarding/website"), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ website_url: websiteUrl }),
      });
      const res = await parseThemeApiResponse(response);
      if (response.ok && !res.isError) {
        toast.success(res.message);
        setStatus((prev) => ({
          ...prev,
          dns: res.data.dns,
          website_url: stripProtocol(res.data.website_url),
        }));
        setWebsiteUrl(stripProtocol(res.data.website_url));
        setStep(3);
        loadStatus();
      } else {
        toast.error(res.message);
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    setLoading(true);
    try {
      const response = await fetch(siteThemeAdminUrl("/onboarding/verify"), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ website_url: websiteUrl }),
      });
      const res = await parseThemeApiResponse(response);
      if (response.ok && !res.isError) {
        if (res.data.verified) {
          if (res.data.provisioning_started) {
            toast.success("Domain verified — provisioning your website...");
            setProvisioning(res.data.provisioning);
            setStep(5);
            loadStatus();
          } else {
            toast.success(res.data.message);
            setStep(5);
            loadStatus();
          }
        } else {
          toast.warn(res.data.message);
        }
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const dns = status?.dns;
  const serverIp = status?.server_ip || status?.dns?.server_ip || "82.112.236.234";

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        zIndex: 10000,
        overflow: "auto",
        padding: 24,
      }}
    >
      <div
        style={{
          maxWidth: 640,
          margin: "40px auto",
          background: "#fff",
          borderRadius: 12,
          padding: 28,
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
        }}
      >
        <h2 style={{ marginTop: 0 }}>Set up {theme.name}</h2>
        <p style={{ color: "#666", marginBottom: 20 }}>
          Complete these steps to connect your domain and go live.
        </p>

        <div style={{ display: "flex", gap: 8, marginBottom: 24, flexWrap: "wrap" }}>
          {STEPS.map((s) => (
            <span
              key={s.id}
              style={{
                fontSize: 12,
                padding: "4px 10px",
                borderRadius: 999,
                background: step >= s.id ? "#2563eb" : "#e5e7eb",
                color: step >= s.id ? "#fff" : "#374151",
              }}
            >
              {s.id}. {s.title}
            </span>
          ))}
        </div>

        {step === 1 && (
          <div>
            <p>Install <strong>{theme.name}</strong> v{theme.version} on your account.</p>
            <button type="button" className="oms-add-btn" onClick={handleInstall} disabled={loading}>
              {loading ? "Installing..." : "Install theme"}
            </button>
          </div>
        )}

        {step === 2 && (
          <div>
            <p>Enter your store domain (no https:// needed).</p>
            <input
              type="text"
              placeholder="yourstore.com"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(stripProtocol(e.target.value))}
              style={{ width: "100%", padding: 10, marginBottom: 12 }}
            />
            <p style={{ fontSize: 13, color: "#666", marginTop: 0 }}>
              Example: <code>demo.100billiontech.com</code>
            </p>
            <button type="button" className="oms-add-btn" onClick={handleSaveWebsite} disabled={loading || !websiteUrl}>
              Save & continue
            </button>
          </div>
        )}

        {step === 3 && (
          <div>
            <p>
              Point your domain DNS to the storefront server. Add these records at your domain
              registrar (GoDaddy, Namecheap, Cloudflare, etc.):
            </p>
            <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 16, fontSize: 14 }}>
              <thead>
                <tr style={{ background: "#f3f4f6" }}>
                  <th style={{ padding: 8, textAlign: "left" }}>Type</th>
                  <th style={{ padding: 8, textAlign: "left" }}>Host</th>
                  <th style={{ padding: 8, textAlign: "left" }}>Value</th>
                </tr>
              </thead>
              <tbody>
                {(dns?.records || [
                  { type: "A", host: "@", value: "82.112.236.234" },
                  { type: "A", host: "www", value: "82.112.236.234" },
                ]).map((row) => (
                  <tr key={`${row.type}-${row.host}`} style={{ borderBottom: "1px solid #e5e7eb" }}>
                    <td style={{ padding: 8 }}>{row.type}</td>
                    <td style={{ padding: 8 }}>{row.host}</td>
                    <td style={{ padding: 8, fontFamily: "monospace" }}>{row.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p style={{ fontSize: 13, color: "#666" }}>
              Server IP: <code>{serverIp}</code> — DNS can take up to 24–48 hours to propagate.
            </p>
            <p style={{ fontSize: 13, color: "#666" }}>{dns?.api_note}</p>
            <button type="button" className="oms-add-btn" onClick={() => setStep(4)}>
              I&apos;ve updated DNS — continue
            </button>
          </div>
        )}

        {step === 4 && (
          <div>
            <p>
              We&apos;ll check that <strong>{stripProtocol(websiteUrl)}</strong> points to the
              Beentara server and is linked to your account.
            </p>
            <button type="button" className="oms-add-btn" onClick={handleVerify} disabled={loading}>
              {loading ? "Checking..." : "Verify domain"}
            </button>
            <button type="button" style={{ marginLeft: 8 }} onClick={() => setStep(3)}>
              Back to DNS instructions
            </button>
          </div>
        )}

        {step === 5 && (
          <div>
            <p>Provisioning your website on the Beentara server. This usually takes a few minutes.</p>
            <div
              style={{
                background: "#f8fafc",
                border: "1px solid #e5e7eb",
                borderRadius: 8,
                padding: 16,
                marginBottom: 16,
              }}
            >
              <p style={{ margin: "0 0 8px", fontWeight: 600 }}>
                {provisioning?.progress?.label || "Starting provisioning..."}
              </p>
              <p style={{ margin: 0, fontSize: 13, color: "#666" }}>
                Step {provisioning?.progress?.current || 0} of {provisioning?.progress?.total || 8}
                {" · "}
                Status: {provisioning?.provision_status || "PROVISIONING"}
                {provisioning?.is_staging && " (staging — final checks running)"}
              </p>
              {provisioning?.last_error && (
                <p style={{ margin: "8px 0 0", color: "#b45309", fontSize: 13 }}>
                  {provisioning.last_error}
                </p>
              )}
            </div>
            <button type="button" onClick={loadStatus} disabled={loading}>
              Refresh status
            </button>
          </div>
        )}

        {step === 6 && (
          <div>
            <p style={{ color: "#059669" }}>Your website is live on your domain.</p>
            <p>Next, customize colors, sections, and branding for your store.</p>
            <button
              type="button"
              className="oms-add-btn"
              onClick={() => {
                onClose();
                navigate("/appearance/customize");
              }}
            >
              Go to Customize
            </button>
          </div>
        )}

        <div style={{ marginTop: 24, textAlign: "right" }}>
          <button type="button" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ThemeInstallWizard;
